/**
 * Vertriebscockpit – alles, was gerade läuft.
 *
 * Eine Tabelle, drei Filterebenen, und alle Zahlen aus derselben Menge:
 * Reiter, Phasen und Personen-Chips rechnen nacheinander auf dasselbe Ergebnis.
 * Vorher widersprachen sich die Kennzahlen im Kopf und die Zahlen darunter,
 * weil sie aus verschiedenen Mengen stammten.
 *
 * „Pausiert" heißt hier nirgends mehr so. Es heißt „Wiedervorlage", weil genau
 * das gemeint ist: Der Vorgang kommt zu einem festen Datum zurück.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconEinstellungen, IconMonday, IconSharepoint } from '../komponenten/Icons';
import { dat } from '../hilfen/format';
import { liegestufe } from '../hilfen/liegezeit';
import type { Angebot } from '../typen';
import './cockpit.css';

type Reiter = 'laufend' | 'wiedervorlage';
type SortSpalte = 'nr' | 'status' | 'liegezeit' | 'zustaendig';

/** Die Phasen als Filter – dieselbe Reihenfolge wie der Weg des Angebots. */
const PHASEN: { schluessel: string; label: string; hinweis: string; stati: string[] }[] = [
  { schluessel: 'arbeit', label: 'in Arbeit', hinweis: 'wird kalkuliert', stati: ['START', 'in Arbeit'] },
  { schluessel: 'technik', label: 'Freigabe Technik', hinweis: 'wartet auf Prüfung', stati: ['Freigabe Technik'] },
  { schluessel: 'gesendet', label: 'Gesendet', hinweis: 'liegt beim Kunden', stati: ['Gesendet', 'Wiedervorlage'] },
];

export function Cockpit() {
  const { nutzer, modalOeffnen } = useKora();
  const meta = useMeta();
  const navigate = useNavigate();
  const [suchParameter, setzeSuchParameter] = useSearchParams();

  const [reiter, setReiter] = useState<Reiter>('laufend');
  const [phase, setPhase] = useState('');
  const [person, setPerson] = useState('');
  const [suche, setSuche] = useState('');
  const [sortSpalte, setSortSpalte] = useState<SortSpalte>('liegezeit');
  const [absteigend, setAbsteigend] = useState(true);

  const abfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  const alle = useMemo(() => abfrage.data ?? [], [abfrage.data]);
  const meinName = nutzer?.name ?? '';

  // Sprung aus dem Team-Überblick: auf die Person filtern.
  useEffect(() => {
    const wer = suchParameter.get('versender');
    if (!wer) return;
    setPerson(wer);
    setzeSuchParameter({}, { replace: true });
  }, [suchParameter, setzeSuchParameter]);

  /** Wem der Vorgang liegt – dieselbe Regel wie in „Meine Aufgaben". */
  const zustaendig = (a: Angebot) => a.versender || a.kalkulant;

  /**
   * Ruht der Vorgang wirklich?
   *
   * Eine fällige Wiedervorlage ist wieder ein laufender Vorgang und gehört in
   * „Laufend" – sonst müsste man zwei Listen im Blick behalten, um zu wissen,
   * was heute dran ist.
   */
  const ruht = (a: Angebot) => a.status === 'Wiedervorlage' && !a.wvFaellig;

  // --- Die eine Menge, aus der alle Zahlen kommen ---------------------------
  const imReiter = useMemo(
    () => alle.filter((a) => a.offen && (reiter === 'wiedervorlage' ? ruht(a) : !ruht(a))),
    [alle, reiter],
  );

  const nachPhase = useMemo(() => {
    if (!phase) return imReiter;
    const stati = PHASEN.find((p) => p.schluessel === phase)?.stati ?? [];
    return imReiter.filter((a) => stati.includes(a.status));
  }, [imReiter, phase]);

  const nachPerson = useMemo(
    () => (person ? nachPhase.filter((a) => zustaendig(a) === person) : nachPhase),
    [nachPhase, person],
  );

  const gefiltert = useMemo(() => {
    const worte = suche.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!worte.length) return nachPerson;
    return nachPerson.filter((a) => {
      const heuhaufen = [a.name, a.kunde.name, a.kunde.ort, a.kategorie, a.prefix, a.status]
        .join(' ')
        .toLowerCase();
      return worte.every((w) => heuhaufen.includes(w));
    });
  }, [nachPerson, suche]);

  // --- Zahlen für Reiter, Phasen und Chips ---------------------------------
  const laufendAnzahl = alle.filter((a) => a.offen && !ruht(a)).length;
  const wvAnzahl = alle.filter((a) => a.offen && ruht(a)).length;

  const phasenZahlen = useMemo(
    () =>
      PHASEN.map((p) => ({
        ...p,
        anzahl: imReiter.filter((a) => p.stati.includes(a.status)).length,
      })).filter((p) => p.anzahl > 0 || p.schluessel === phase),
    [imReiter, phase],
  );

  /**
   * Personen-Chips – nur für die, die in dieser Ansicht überhaupt etwas haben.
   * Der eigene steht vorn und ist gekennzeichnet.
   */
  const personen = useMemo(() => {
    const zaehler = new Map<string, number>();
    for (const a of nachPhase) {
      const wer = zustaendig(a);
      if (wer) zaehler.set(wer, (zaehler.get(wer) ?? 0) + 1);
    }
    return [...zaehler.entries()]
      .map(([name, anzahl]) => ({ name, anzahl, ich: name === meinName }))
      .sort((x, y) => Number(y.ich) - Number(x.ich) || y.anzahl - x.anzahl);
  }, [nachPhase, meinName]);

  // --- Sortierung -----------------------------------------------------------
  const liste = useMemo(() => {
    const kopie = [...gefiltert];
    kopie.sort((a, b) => {
      let v = 0;
      if (sortSpalte === 'nr') v = a.nr - b.nr;
      else if (sortSpalte === 'status') v = a.status.localeCompare(b.status, 'de');
      else if (sortSpalte === 'zustaendig') v = zustaendig(a).localeCompare(zustaendig(b), 'de');
      else v = a.alter - b.alter;
      return absteigend ? -v : v;
    });
    return kopie;
  }, [gefiltert, sortSpalte, absteigend]);

  function sortiereNach(spalte: SortSpalte) {
    if (spalte === sortSpalte) setAbsteigend((a) => !a);
    else {
      setSortSpalte(spalte);
      setAbsteigend(true);
    }
  }

  return (
    <div className="cp">
      <div className="cp-kopf">
        <div>
          <div className="cp-kicker">Pipeline</div>
          <h1 className="cp-titel">Vertriebscockpit</h1>
          <div className="cp-untertitel">
            Alles, was jetzt läuft – von der Kalkulation bis zum Kunden.
          </div>
        </div>
        <div className="cp-kopfaktionen">
          <button type="button" className="cp-neu" onClick={() => navigate('/neu')}>
            + Neues Angebot
          </button>
          {nutzer?.istAdmin && (
            <button
              type="button"
              className="cp-zahnrad"
              title="Einstellungen"
              onClick={() => modalOeffnen({ art: 'einstellungen' })}
            >
              <IconEinstellungen groesse={17} />
            </button>
          )}
        </div>
      </div>

      {/* Reiter: laufend oder ruhend. Die Zahl steht dabei. */}
      <div className="cp-reiterzeile">
        <div className="cp-reiter">
          <button
            type="button"
            className={reiter === 'laufend' ? 'aktiv' : ''}
            onClick={() => {
              setReiter('laufend');
              setPhase('');
            }}
          >
            Laufend<span className="cp-reiter-zahl">{laufendAnzahl}</span>
          </button>
          <button
            type="button"
            className={reiter === 'wiedervorlage' ? 'aktiv' : ''}
            onClick={() => {
              setReiter('wiedervorlage');
              setPhase('');
            }}
          >
            Wiedervorlage<span className="cp-reiter-zahl">{wvAnzahl}</span>
          </button>
        </div>
        <span className="cp-stand">Stand {meta.heuteText}</span>
      </div>

      {/* Phasen sind Filter, keine Dekoration. */}
      <div className="cp-phasenzeile">
        <div className="cp-phasen">
          {phasenZahlen.map((p) => (
            <button
              key={p.schluessel}
              type="button"
              className={`cp-phase ${phase === p.schluessel ? 'aktiv' : ''}`}
              onClick={() => setPhase(phase === p.schluessel ? '' : p.schluessel)}
            >
              <span className="cp-phase-zahl">{p.anzahl}</span>
              <span className="cp-phase-text">
                <span className="cp-phase-label">{p.label}</span>
                <span className="cp-phase-hinweis">{p.hinweis}</span>
              </span>
            </button>
          ))}
        </div>

        <label className="cp-suche">
          <span className="cp-suche-lupe" aria-hidden="true">⌕</span>
          <input
            type="search"
            value={suche}
            placeholder="Nummer, Kunde, Ort, Gewerk"
            aria-label="Angebote durchsuchen"
            onChange={(e) => setSuche(e.target.value)}
          />
          {suche && (
            <button type="button" className="cp-suche-weg" onClick={() => setSuche('')} title="Suche zurücksetzen">
              ×
            </button>
          )}
        </label>
      </div>

      {/* Personen-Chips – nur wer hier Vorgänge hat. */}
      <div className="cp-chips">
        <button
          type="button"
          className={`cp-chip ${person === '' ? 'aktiv' : ''}`}
          onClick={() => setPerson('')}
        >
          Alle<span className="cp-chip-zahl">{nachPhase.length}</span>
        </button>
        {personen.map((p) => (
          <button
            key={p.name}
            type="button"
            className={`cp-chip ${person === p.name ? 'aktiv' : ''}`}
            onClick={() => setPerson(person === p.name ? '' : p.name)}
          >
            {p.name}
            {p.ich ? ' (du)' : ''}
            <span className="cp-chip-zahl">{p.anzahl}</span>
          </button>
        ))}
      </div>

      <div className="cp-tabelle-rahmen">
        <div className="cp-tabelle">
          <div className="cp-zeile cp-kopfzeile">
            <Kopf spalte="nr" aktiv={sortSpalte} absteigend={absteigend} onSort={sortiereNach}>
              Angebot
            </Kopf>
            <Kopf spalte="status" aktiv={sortSpalte} absteigend={absteigend} onSort={sortiereNach}>
              Status
            </Kopf>
            <Kopf spalte="liegezeit" aktiv={sortSpalte} absteigend={absteigend} onSort={sortiereNach}>
              Liegezeit
            </Kopf>
            <Kopf spalte="zustaendig" aktiv={sortSpalte} absteigend={absteigend} onSort={sortiereNach}>
              Zuständig
            </Kopf>
            <div>Nächster Schritt</div>
            <div className="cp-rechts">Links</div>
          </div>

          {liste.map((a) => (
            <Zeile
              key={a.id}
              a={a}
              onOeffnen={() => navigate(`/angebot/${a.id}`, { state: { herkunft: 'cockpit' } })}
              gruenBis={meta.liegezeit.gruenBis}
              orangeBis={meta.liegezeit.orangeBis}
            />
          ))}

          {liste.length === 0 && (
            <div className="cp-leer">
              {suche
                ? `Kein Treffer für „${suche}".`
                : reiter === 'wiedervorlage'
                  ? 'Kein Angebot liegt auf Wiedervorlage.'
                  : 'Kein laufendes Angebot in dieser Auswahl.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Sortierbare Kopfzelle. */
function Kopf({
  spalte,
  aktiv,
  absteigend,
  onSort,
  children,
}: {
  spalte: SortSpalte;
  aktiv: SortSpalte;
  absteigend: boolean;
  onSort: (s: SortSpalte) => void;
  children: React.ReactNode;
}) {
  const ist = aktiv === spalte;
  return (
    <button type="button" className={`cp-kopfknopf ${ist ? 'aktiv' : ''}`} onClick={() => onSort(spalte)}>
      {children}
      <span className="cp-sortpfeil">{ist ? (absteigend ? '▾' : '▴') : ''}</span>
    </button>
  );
}

/**
 * Was als Nächstes ansteht – im Klartext.
 *
 * Das Cockpit sagt, dass etwas dran ist und seit bzw. ab wann. Wie nachgefasst
 * wird – Mail oder Anruf – entscheidet der Vertrieb in „Meine Aufgaben" je Kunde.
 */
function naechsterSchritt(a: Angebot): { text: string; unten: string; dringend: boolean } {
  if (a.wvFaellig && a.wiedervorlage) {
    return {
      text: 'Wiedervorlage fällig',
      unten: `vereinbart für ${dat(a.wiedervorlage)}`,
      dringend: true,
    };
  }
  if (a.status === 'Wiedervorlage' && a.wiedervorlage) {
    return { text: 'Wiedervorlage', unten: `vereinbart für ${dat(a.wiedervorlage)}`, dringend: false };
  }
  if (a.beimKunden) {
    if (a.nachfass.anrufFaellig) {
      return { text: 'Nachfassen fällig', unten: `seit ${dat(a.nachfass.anrufAb)}`, dringend: true };
    }
    return { text: 'liegt beim Kunden', unten: `Nachfassen ab ${dat(a.nachfass.anrufAb)}`, dringend: false };
  }
  if (a.status === 'Freigabe Technik') {
    return { text: 'wartet auf technische Prüfung', unten: 'noch nicht beim Kunden', dringend: false };
  }
  return { text: 'Kalkulation läuft', unten: 'noch nicht beim Kunden', dringend: false };
}

/**
 * Farbe der Liegezeit – nur bei versendeten Angeboten.
 *
 * Ein Angebot in Kalkulation ist nach 20 Tagen nicht dramatisch, ein
 * versendetes schon. Farbe als Eskalation, nicht als Dekoration.
 *
 * Die Bänder kommen zentral aus den Einstellungen, die Farbe aus derselben
 * Funktion wie in „Meine Aufgaben". Vorher standen hier eigene Schwellen im
 * Code – derselbe Vorgang sah in zwei Masken unterschiedlich dringend aus.
 */
function liegeTon(a: Angebot, gruenBis: number, orangeBis: number): string {
  if (!a.beimKunden) return '';
  return liegestufe(a.alter, gruenBis, orangeBis);
}

function Zeile({
  a,
  onOeffnen,
  gruenBis,
  orangeBis,
}: {
  a: Angebot;
  onOeffnen: () => void;
  gruenBis: number;
  orangeBis: number;
}) {
  const schritt = naechsterSchritt(a);
  const wer = a.versender || a.kalkulant;
  const initialen = a.versender ? a.versInitialen : a.kalkInitialen;

  return (
    <div
      className="cp-zeile cp-datenzeile"
      role="button"
      tabIndex={0}
      onClick={onOeffnen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOeffnen();
      }}
    >
      {/* Kunde und Ort stehen unter der Nummer – dort ist Breite. Als eigene
          Spalte war der Ort abgeschnitten und doppelt, weil er auch im
          Angebotsnamen steckt. */}
      <div className="cp-angebot">
        <div className="cp-nummer">{a.name}</div>
        <div className="cp-kunde">
          {a.kunde.name}
          {a.kunde.ort ? ` · ${a.kunde.ort}` : ''}
        </div>
      </div>

      <div>
        <span className="cp-status" title={`Seit ${a.alterText} in diesem Status`}>
          {a.status}
        </span>
      </div>

      {/* Eine Bedeutung: Tage im aktuellen Status, darunter das Bezugsdatum. */}
      <div className="cp-liegezeit">
        <div className={`cp-tage ${liegeTon(a, gruenBis, orangeBis)}`}>{a.alterText}</div>
        <div className="cp-seit">seit {a.statusDatumText}</div>
      </div>

      <div className="cp-person" title={wer}>
        <span className="cp-avatar">{initialen}</span>
        <span className="cp-personname">{wer}</span>
      </div>

      <div className="cp-schritt">
        <div className={`cp-schritt-text ${schritt.dringend ? 'dringend' : ''}`}>{schritt.text}</div>
        <div className="cp-schritt-unten">{schritt.unten}</div>
      </div>

      <div className="cp-links cp-rechts" onClick={(e) => e.stopPropagation()}>
        {a.mondayUrl ? (
          <a href={a.mondayUrl} target="_blank" rel="noreferrer" title="Monday-Element öffnen">
            <IconMonday groesse={15} />
          </a>
        ) : (
          <span className="cp-link-leer" title="Kein Monday-Element verknüpft">
            <IconMonday groesse={15} />
          </span>
        )}
        <a
          href={`kora://oeffne?art=angebot&ordner=${encodeURIComponent(a.ordnerName)}`}
          title={`${a.ordnerName} im Explorer öffnen`}
        >
          <IconSharepoint groesse={15} />
        </a>
      </div>
    </div>
  );
}